import { termlyWebsiteUuidSchema } from "@loot-os/api-contract";
import { Button } from "@loot-os/ui/components/button";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import type { ReactNode } from "react";
import { useState } from "react";

/**
 * The Termly website a storefront's cookie banner runs on, as the UUID from Termly's embed
 * snippet. Shared by the platform default (`/legal`) and an environment's override
 * (Storefront → Legal / Consent): the two differ only in whether a blank may be saved (the
 * platform default may be cleared, which turns the banner off wherever nothing overrides it; an
 * environment goes back to the default through `footer` instead). Remount with `key` when the
 * saved value changes, like the loser prize form.
 */
export function TermlyWebsiteForm({
  websiteUuid,
  canEdit,
  saving,
  allowEmpty = false,
  placeholder,
  onSave,
  footer,
}: {
  websiteUuid: string;
  canEdit: boolean;
  saving: boolean;
  /** Whether saving with the field blank is a valid save (no banner). */
  allowEmpty?: boolean;
  placeholder?: string;
  onSave: (websiteUuid: string) => void;
  footer?: ReactNode;
}) {
  const [draft, setDraft] = useState(websiteUuid);
  const trimmed = draft.trim();
  const dirty = trimmed !== websiteUuid;
  // The same check the API runs, so a paste of the whole script URL is refused here first.
  const valid = trimmed === "" ? allowEmpty : termlyWebsiteUuidSchema.safeParse(trimmed).success;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    onSave(trimmed);
  };

  return (
    <form className="grid gap-5" onSubmit={submit}>
      <div className="grid gap-2">
        <Label htmlFor="termly-website-uuid">Termly website UUID</Label>
        <Input
          id="termly-website-uuid"
          value={draft}
          disabled={!canEdit}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder ?? "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"}
          spellCheck={false}
          autoComplete="off"
        />
        <p className="text-xs text-muted-foreground">
          The id in the embed snippet Termly gives the website:{" "}
          <code className="font-mono">app.termly.io/resource-blocker/&lt;this id&gt;</code>. Just
          the id, not the whole URL.
          {allowEmpty ? " Leave it blank for no cookie banner." : ""}
        </p>
        {trimmed !== "" && !valid && (
          <p className="text-xs text-destructive">That is not a Termly website UUID.</p>
        )}
      </div>
      {canEdit && (
        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" disabled={saving || !dirty || !valid}>
            {saving ? "Saving…" : "Save website"}
          </Button>
          {footer}
        </div>
      )}
    </form>
  );
}
